import React, { useCallback, useEffect, useRef } from 'react';
import { matchPath, useLocation } from 'react-router';
import styled from 'styled-components';

import { FullPageModal } from '#/components/Modal';
import PlanPicker from '#/components/PlanPicker/PlanPicker';
import { Status } from '#/components/Status';
import { useNavigation } from '#/hooks/useNavigation';
import { usePaywall } from '#/hooks/usePaywall';
import { Path } from '#/service/routes';

import { usePlanPickerActions, usePlans } from './hooks';
import { useSubscriptionParameters } from './utils';

const HiddenContainer = styled.div`
  display: none;
`;

const Paywall = React.memo(() => {
  const {
    isShowing,
    deferredPromise: { resolve: resolvePayment } = {},
    error,
    setError,
    api,
    closePaywall,
  } = usePaywall();
  const { pathname } = useLocation();
  const prevPathName = useRef<string>(pathname);
  const { goBack } = useNavigation('Paywall');

  const customParameters = useSubscriptionParameters();
  const plans = usePlans({ api, onError: setError, error, isShowing });
  const actions = usePlanPickerActions();

  /**
   * TODO: Actually instead of this, we should have a handler for clicking App Logo on the Modal
   * and then close the Modal. It is quite strange that we navigate user to the home page.
   * We should just hide the full-page Modal.
   */
  useEffect(() => {
    // Only react to the location change, nothing else. Otherwise we will be closing modal just after opening it
    if (isShowing && prevPathName.current !== pathname) {
      closePaywall();
    }
    prevPathName.current = pathname;
  }, [closePaywall, isShowing, pathname]);

  const handleErrorDismiss = useCallback(() => {
    setError(undefined);

    // Navigate to the previous page only if the paywall error
    // was dismissed on the playback page
    if (matchPath(Path.Playback, pathname)) {
      goBack();
    } else {
      closePaywall();
    }
  }, [closePaywall, goBack, pathname, setError]);

  const showPaywall = (isShowing && plans) || error;

  return (
    <div>
      <HiddenContainer className="hidden-paywall-container" />
      {showPaywall && (
        <FullPageModal>
          {error && (
            <Status
              fatalError
              onDismiss={handleErrorDismiss}
              statusMessage={error.message}
              statusTitle={error.title}
            />
          )}
          <PlanPicker
            actions={actions}
            customParameters={customParameters}
            hasError={!!error}
            onResult={resolvePayment}
            plans={plans || []}
          />
        </FullPageModal>
      )}
    </div>
  );
});

Paywall.displayName = 'Paywall';

export default Paywall;
